import { Empty, Image, Pagination, Spin } from 'antd'
import { useRolodexSignedURLs } from 'features/rolodex'
import React, { useMemo, useState } from 'react'
import styled from 'styled-components'

const ExplorerContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 60vh;
  max-height: 70vh;
  background-color: ${({ theme }) => theme.forecast.background1};
`

const Header = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.forecast.background4};
`

const Title = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.activeText};
  margin: 0;
`

const PhotoGrid = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 1rem;
  align-content: start;
`

const PhotoCard = styled.div`
  position: relative;
  aspect-ratio: 1;
  border-radius: 0.5rem;
  overflow: hidden;
  cursor: pointer;
  background-color: ${({ theme }) => theme.forecast.background2};
  border: 1px solid ${({ theme }) => theme.forecast.background4};
  transition: all 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.forecast.primary};
    transform: scale(1.05);
  }
`

const PhotoImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const PhotoName = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 0.5rem;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent);
  color: white;
  font-size: 0.75rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const PaginationContainer = styled.div`
  padding: 1rem 1.5rem;
  border-top: 1px solid ${({ theme }) => theme.forecast.background4};
  display: flex;
  justify-content: center;
`

const PHOTOS_PER_PAGE = 100

interface PhotoExplorerProps {
  entryId: string
  photoFilenames: string[]
}

export const PhotoExplorer: React.FC<PhotoExplorerProps> = ({
  entryId,
  photoFilenames,
}) => {
  const [currentPage, setCurrentPage] = useState(1)

  const paginatedPhotos = useMemo(() => {
    const startIndex = (currentPage - 1) * PHOTOS_PER_PAGE
    const endIndex = startIndex + PHOTOS_PER_PAGE
    return photoFilenames.slice(startIndex, endIndex)
  }, [photoFilenames, currentPage])

  const signedUrlRequests = useMemo(() => {
    return paginatedPhotos.map((filename) => ({
      entry_id: entryId,
      path: `photos/${filename}`,
    }))
  }, [paginatedPhotos, entryId])

  const { results, isLoading } = useRolodexSignedURLs(signedUrlRequests)

  const photoUrls = useMemo(() => {
    return paginatedPhotos.map((filename, index) => ({
      filename,
      url: results[index]?.signed_url,
    }))
  }, [paginatedPhotos, results])

  return (
    <ExplorerContainer>
      <Header>
        <Title>Photos ({photoFilenames.length} total)</Title>
      </Header>

      {isLoading && (
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <Spin tip='Loading photos...' />
        </div>
      )}

      {!isLoading && photoFilenames.length === 0 && (
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <Empty description='No photos uploaded yet' />
        </div>
      )}

      {!isLoading && photoFilenames.length > 0 && (
        <>
          <PhotoGrid>
            <Image.PreviewGroup>
              {photoUrls.map((photo, index) =>
                photo.url ? (
                  <PhotoCard key={index}>
                    <Image
                      src={photo.url}
                      alt={photo.filename}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                      preview={{
                        mask: (
                          <div>
                            <span>View</span>
                          </div>
                        ),
                      }}
                    />
                    <PhotoName>{photo.filename}</PhotoName>
                  </PhotoCard>
                ) : (
                  <PhotoCard key={index}>
                    <PhotoImage
                      src='data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext fill="rgba(0,0,0,0.5)" font-family="sans-serif" font-size="14" dy="10.5" font-weight="bold" x="50%25" y="50%25" text-anchor="middle"%3ELoading...%3C/text%3E%3C/svg%3E'
                      alt='Loading...'
                    />
                    <PhotoName>{photo.filename}</PhotoName>
                  </PhotoCard>
                )
              )}
            </Image.PreviewGroup>
          </PhotoGrid>

          {photoFilenames.length > PHOTOS_PER_PAGE && (
            <PaginationContainer>
              <Pagination
                current={currentPage}
                total={photoFilenames.length}
                pageSize={PHOTOS_PER_PAGE}
                onChange={setCurrentPage}
                showSizeChanger={false}
                showTotal={(total, range) =>
                  `${range[0]}-${range[1]} of ${total} photos`
                }
              />
            </PaginationContainer>
          )}
        </>
      )}
    </ExplorerContainer>
  )
}
